import React from 'react'
import styles from './OnboardingNext.module.scss'
import ToggleButtonGroupController from '../../components/ToggleButtonGroup/ToggleButtonGroupController'
import { useForm, Controller } from 'react-hook-form'
import SimpleInputController from '../../components/simpleInput/SimpleInputController'
import Overseas from './Components/Overseas'
import { useState, useEffect, useRef } from 'react'
import IncentiveModal from './IncentiveModal'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useAuthStatus, useToken } from '../../utils'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { Tooltip } from 'react-tooltip'
import OverseasMatchModal from './OverseasMatchModal'
import { useStateMachine } from 'little-state-machine'
import { updateStore } from '../../store'
import { updateUserProfileImage, savePreferredContract, fetchTours } from '../../services/api'
import { formatIncentiveTaxOptionLabel } from '../../utils/incentiveDisplay'

const OnboardingNext = ({ noMarginTop = false, mainFormMarginTop24px = false }) => {
	const { actions, state } = useStateMachine({ updateStore })
	const [authStatus] = useAuthStatus()

	const navigate = useNavigate()

	// 드롭다운 관련 상태들
	const [isDropdownOpen, setIsDropdownOpen] = useState(false)
	const [isDropdownItemSelected, setIsDropdownItemSelected] = useState(false)
	const dropdownRef = useRef(null)

	const [isOverseasMatchModalOpen, setIsOverseasMatchModalOpen] = useState(false)

	// 해외대회 지원 항목 옵션
	const overseasSupportOptions = [
		{ label: '미지원', value: 0 },
		{ label: '10%', value: 10 },
		{ label: '30%', value: 30 },
		{ label: '50%', value: 50 },
		{ label: '70%', value: 70 },
		{ label: '90%', value: 90 },
		{ label: '100%', value: 100 },
	]

	// 드롭다운 토글 함수
	const toggleDropdown = () => {
		setIsDropdownOpen(!isDropdownOpen)
	}

	// 드롭다운 외부 클릭 시 닫기
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsDropdownOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	const {
		control,
		handleSubmit,
		register,
		watch,
		getValues,
		setValue,
		trigger,
		formState: { errors, isValid },
	} = useForm({
		mode: 'onChange', // 실시간 유효성 검사를 위해 'onChange' 모드 사용을 권장합니다.
		defaultValues: {
			preferWhere: 'local',
			local: {
				applyTax: 'before',
				payPeriod: 'week',
				cutPass: '',
				cutOut: '',
				incentiveOption: '',
				incentiveSettings: [],
				sponsorCap: true,
				jejudo: true,
				jejudoMoney: '',
			},
			global: {
				applyTax: 'before',
				payPeriod: 'week',
				cutPass: '',
				cutOut: '',
				incentiveOption: '',
				incentiveSettings: [],
				sponsorCap: true,
				overseasSupport: null,
			},
		},
	})

	const preferWhere = watch('preferWhere')
	const localData = watch('local')
	const globalData = watch('global')

	const activeData = preferWhere === 'local' ? localData : globalData

	const jejudo = watch('local.jejudo')
	const jejudoMoney = watch('local.jejudoMoney')

	const localIncentiveOption = watch('local.incentiveOption')
	const globalIncentiveOption = watch('global.incentiveOption')

	const [isLocalValid, setIsLocalValid] = useState(false)
	const [isGlobalValid, setIsGlobalValid] = useState(false)

	const token = useToken()

	useEffect(() => {
		if (preferWhere === 'local') {
			setIsLocalValid(isValid)
		}
	}, [isValid, preferWhere])

	useEffect(() => {
		if (preferWhere === 'global') {
			setIsGlobalValid(isValid)
		}
	}, [isValid, preferWhere])

	// preferWhere 변경 시 유효성 검사 실행
	useEffect(() => {
		if (preferWhere === 'local') {
			trigger('local')
		} else if (preferWhere === 'global') {
			trigger('global')
		}
	}, [preferWhere, trigger])

	const preferWhereOption = [
		{ label: '국내', value: 'local' },
		{ label: '해외', value: 'global' },
	]

	const applyTaxOption = [
		{ label: '세전', value: 'before' },
		{ label: '세후', value: 'after' },
	]

	const payPeriodOption = [
		{ label: '주급', value: 'week' },
		{ label: '일시불제(기간계약)', value: 'single' },
	]

	const jejudoOption = [
		{ label: '지원', value: true },
		{ label: '미지원', value: false },
	]

	const sponsorCapOption = [
		{ label: '착용', value: true },
		{ label: '미착용', value: false },
	]

	const [isModalOpen, setIsModalOpen] = useState(false)
	// 인센티브 입력하기 -> 인센티브 모달 창 팝업
	const handleModalOpen = () => {
		setIsModalOpen(true)
	}
	// 인센티브 모달 뒤로가기
	const handleReturn = () => {
		setIsModalOpen(false)
	}
	// 인센티브 모달 완료시
	const handleFinishSubmit = (option, settingsData) => {
		setValue(`${preferWhere}.incentiveOption`, option)
		setValue(`${preferWhere}.incentiveSettings`, settingsData, { shouldValidate: true })
		setIsModalOpen(false)
	}

	// 해외 매치 모달 뒤로가기
	const handleOverseasMatchReturn = () => {
		setIsOverseasMatchModalOpen(false)
	}

	// 해외 매치 모달에서 확인 버튼 클릭 시 제출
	const handleOverseasMatchSubmit = () => {
		const currentData = getValues()
		const skipData = { ...currentData, _isGlobalSkipped: true }
		onSubmit(skipData)
		setIsOverseasMatchModalOpen(false)
	}

	const onSubmit = async (data) => {
		const transformedData = { ...data }

		// 1. local.cutPass 변환
		if (transformedData.local && transformedData.local.cutPass) {
			transformedData.local.cutPass = Number(
				String(transformedData.local.cutPass).replace(/[^0-9]/g, ''),
			)
		}

		// 2. local.cutOut 변환
		if (transformedData.local && transformedData.local.cutOut) {
			transformedData.local.cutOut = Number(
				String(transformedData.local.cutOut).replace(/[^0-9]/g, ''),
			)
		}

		// 3. local.jejudoMoney 변환
		if (transformedData.local && transformedData.local.jejudoMoney) {
			transformedData.local.jejudoMoney = Number(
				String(transformedData.local.jejudoMoney).replace(/[^0-9]/g, ''),
			)
		}

		// 4. global.cutPass 변환
		if (transformedData.global && transformedData.global.cutPass) {
			transformedData.global.cutPass = Number(
				String(transformedData.global.cutPass).replace(/[^0-9]/g, ''),
			)
		}
		// 5. global.cutOut 변환
		if (transformedData.global && transformedData.global.cutOut) {
			transformedData.global.cutOut = Number(
				String(transformedData.global.cutOut).replace(/[^0-9]/g, ''),
			)
		}

		// console.log('데이터 입력 완료 : ', transformedData)

		const domesticContractData = {
			isTaxIncluded:
				transformedData.local.applyTax !== ''
					? transformedData.local.applyTax === 'after'
						? true
						: false
					: null,
			weeklySalary: transformedData.local.cutPass, // 폼 필드에 없는 정보 -> api 수정 필요
			weeklySalaryFail: transformedData.local.cutOut, // 폼 필드에 없는 정보 -> api 수정 필요
			payrollCycles:
				transformedData.local.payPeriod !== ''
					? transformedData.local.payPeriod === 'week'
						? 'weekly'
						: 'perGame'
					: null,
			// payrollCycles에서 anuual 이라는 선택지는 없음 -> api 수정 필요
			isExpenseReimbursementJeju: transformedData.local.jejudo,
			expenseReimbursementJeju: transformedData.local.jejudo
				? Number(transformedData.local.jejudoMoney)
				: 0, // 미지원 하면 0으로 일단 설정함
			wearSponsoredHat: transformedData.local.sponsorCap,
			prizeType:
				transformedData.local.incentiveOption === ''
					? null
					: transformedData.local.incentiveOption === 'amountReceived'
						? 'actual'
						: 'displayed',
			incentives: transformedData.local.incentiveSettings.map((option) => ({
				startRank: option.start,
				endRank: option.end === 'CUT' ? 0 : option.end,
				percentage: option.percentage,
			})),
			// incentiveOption 누락 : 상금 표기액 , 상금 실수령액 선택 조건
		}

		const overseasContractData = {
			// 폼 필드에 없는 정보 -> api 수정 필요
			isTaxIncluded:
				transformedData.global.applyTax !== ''
					? transformedData.global.applyTax === 'after'
						? true
						: false
					: null,
			weeklySalary: transformedData.global.cutPass, // 폼 필드에 없는 정보 -> api 수정 필요
			weeklySalaryFail: transformedData.global.cutOut, // 폼 필드에 없는 정보 -> api 수정 필요
			payrollCycles:
				transformedData.global.payPeriod !== ''
					? transformedData.global.payPeriod === 'week'
						? 'weekly'
						: 'perGame'
					: null,
			// payrollCycles에서 anuual 이라는 선택지는 없음 -> api 수정 필요
			expenseReimbursement: transformedData.global.overseasSupport,
			wearSponsoredHat: transformedData.global.sponsorCap,
			prizeType:
				transformedData.global.incentiveOption === ''
					? null
					: transformedData.global.incentiveOption === 'amountReceived'
						? 'actual'
						: 'displayed',
			incentives: transformedData.global.incentiveSettings.map((option) => ({
				startRank: option.start,
				endRank: option.end === 'CUT' ? 0 : option.end,
				percentage: option.percentage,
			})),
			// incentiveOption 누락 : 상금 표기액, 상금 실수령액 선택 조건
		}

		const dataReadyToSubmit = {
			// 컷통과 금액, 컷 패스 금액 누락
			contract: {},
		}

		// 국내는 필수, 해외는 선택
		// _isGlobalSkipped 속성이 있으면 국내만, 없으면 둘 다
		if (data._isGlobalSkipped) {
			dataReadyToSubmit.region = 'DOMESTIC'
		} else {
			dataReadyToSubmit.region = 'BOTH'
		}

		// 국내 계약 데이터는 항상 추가
		dataReadyToSubmit.contract.domesticContract = domesticContractData

		// 글로벌을 스킵하지 않았다면 해외 계약 데이터 추가
		if (!data._isGlobalSkipped) {
			dataReadyToSubmit.contract.overseasContract = overseasContractData
		}

		console.log('유저상태 : ', authStatus)
		console.log('서버에 전송할 최종 데이터 : ', dataReadyToSubmit)

		try {
			console.log('dataReadyToSubmit : ', dataReadyToSubmit)
			console.log('token : ', token)
			const response = await savePreferredContract(token, dataReadyToSubmit)
			console.log('서버 응답:', response)
			console.log('제출 성공! 홈으로 이동합니다.')
			navigate('/')
		} catch (error) {
			// await 중 에러가 발생하면 이 부분이 실행됩니다.
			console.error('데이터 전송 실패:', error)

			// 에러 상세 정보 출력 및 사용자에게 피드백 제공
			if (error.response) {
				console.error(
					'서버 응답 에러 (데이터, 상태):',
					error.response.data,
					error.response.status,
				)
				alert(
					`데이터 저장 실패: ${error.response.data.message || '알 수 없는 서버 오류입니다.'}`,
				)
			} else if (error.request) {
				console.error('응답 없음 (네트워크 오류?):', error.request)
				alert('네트워크 오류: 서버에 연결할 수 없습니다.')
			} else {
				console.error('요청 설정 중 오류:', error.message)
				alert('요청 중 문제가 발생했습니다.')
			}
		}
	}

	const isOverallFormValid = isLocalValid || isGlobalValid

	const formatNumberWithCommas = (value) => {
		if (value === null || value === undefined || value === '') return ''
		// 숫자가 아닌 문자를 모두 제거하여 순수 숫자 문자열만 남깁니다.
		const numericValue = String(value).replace(/[^0-9]/g, '')
		if (numericValue === '') return ''

		// 숫자로 변환 후, 한국 로케일 기준으로 천 단위 쉼표 추가
		return new Intl.NumberFormat('ko-KR').format(Number(numericValue))
	}

	const handlePriceInputChange = (event, rkHookFormOnChange) => {
		const inputValue = event.target.value // 사용자가 input에 입력한 원본 값

		// 숫자만 허용하도록 입력 값 정제 (숫자가 아닌 문자 제거)
		const sanitizedValue = inputValue.replace(/[^0-9]/g, '')

		//  정제된 값에 천 원 단위 쉼표 포맷팅 적용
		const formattedValue = formatNumberWithCommas(sanitizedValue)

		//  react-hook-form의 field.onChange를 호출하여 폼 상태 업데이트
		//      이때, UI에 표시될 포맷팅된 값을 넘겨줍니다.
		rkHookFormOnChange(formattedValue)
	}

	return (
		<div className={`${styles.container} ${noMarginTop ? styles.noMarginTop : ''}`}>
			<div
				className={`${styles.mainForm} ${mainFormMarginTop24px ? styles.mainFormMarginTop24px : ''}`}>
				<div className={styles.contractType}>선호하는 계약 정보를 입력해주세요</div>
				{/* <div className={styles.chooseIsDomestic}>
					<ToggleButtonGroupController
						control={control}
						name="preferWhere"
						label=""
						toggleOption={preferWhereOption}
						styleMode={styles}
						rules={{ required: '선호하는 계약 종류를 선택해주세요.' }}
					/>
				</div> */}

				{preferWhere === 'local' && (
					<>
						<div className={styles.displayRegion}>국내</div>
						<div className={styles.applyTax}>
							<ToggleButtonGroupController
								control={control}
								name="local.applyTax"
								toggleFieldLabel="임금 세금 포함 여부"
								isNoticeNeed={true}
								isNoticeNeedText={`세전 : 3.3% 제외 후 입금 예정\n세후 : 3.3% 제외된 실 입금 금액`}
								toggleOption={applyTaxOption}
								rules={{ required: '임금 세금포함여부 골라주세요' }}
							/>
						</div>
						<div className={styles.payPeriod}>
							<ToggleButtonGroupController
								control={control}
								name="local.payPeriod"
								toggleFieldLabel="임금 지급 주기"
								toggleOption={payPeriodOption}
								rules={{ required: '임금 지급 주기 골라주세요' }}
							/>
						</div>
						<div className={styles.cutPass}>
							<SimpleInputController
								control={control}
								name="local.cutPass"
								rules={{ required: '컷 통과 금액을 입력해주세요' }}
								inputLabel="컷통과"
								inputPlaceholder="컷 통과시 지급할 금액을 입력해주세요"
								onChange={handlePriceInputChange}
							/>
						</div>
						<div className={styles.cutOut}>
							<SimpleInputController
								control={control}
								name="local.cutOut"
								rules={{ required: '컷 탈락 금액을 입력해주세요' }}
								inputLabel="컷탈락"
								inputPlaceholder="컷 탈락시 지급할 금액을 입력해주세요"
								onChange={handlePriceInputChange}
							/>
						</div>
						<div className={styles.incentive}>
							<div className={styles.incentiveInfo}>
								인센티브
								<InformationCircleIcon
									className={styles.noticeIcon}
									data-tooltip-id="my-tooltip2"
									data-tooltip-content={`상금 표기액 : 각 투어별 홈페이지에 명시된 금액 \n 상금 실수령액 : KPGA 10%, KLPGA 9.3% 제외된 금액`}
								/>
								<Tooltip id="my-tooltip2" place="top" className={styles.tooltip} />
							</div>
							<button type="button" onClick={handleModalOpen}>
								입력하기
							</button>
						</div>
						<Controller
							name="local.incentiveSettings"
							control={control}
							rules={{
								validate: (value) =>
									value && value.length > 0
										? true
										: '인센티브 설정을 입력해주세요.',
							}}
							render={({ field }) => <input type="hidden" {...field} />}
						/>
						{activeData?.incentiveSettings &&
							activeData?.incentiveSettings.length > 0 && (
								<div className={styles.incentiveDisplaySection}>
									<div className={styles.incentiveDisplayItem}>
										{formatIncentiveTaxOptionLabel(localIncentiveOption)}
									</div>
									{activeData.incentiveSettings.map((setting) => {
										if (setting.percentage === null && setting.end === null)
											return null
										const displayRank =
											setting.id === 1
												? '우승 '
												: setting.start +
													(setting.end !== null
														? ` ~ ${setting.end} `
														: '~ ')
										const displayPercentage =
											setting.percentage !== null
												? `${setting.percentage}%`
												: '미입력'
										return (
											<div
												key={setting.id}
												className={styles.incentiveDisplayItem}>
												<span>{displayRank}</span>
												<span>: {displayPercentage}</span>
											</div>
										)
									})}
								</div>
							)}

						<div className={styles.jejudo}>
							<ToggleButtonGroupController
								control={control}
								name="local.jejudo"
								isNoticeNeed={true}
								isNoticeNeedText={`제주대회 출전 시 : 세후 금액으로 3.3%으로 제외된 금액`}
								toggleFieldLabel="제주대회 출전 시"
								toggleOption={jejudoOption}
							/>
							<div className={`${jejudo ? styles.jejudoInput : styles.none}`}>
								<SimpleInputController
									control={control}
									name="local.jejudoMoney"
									isLabel={false}
									inputPlaceholder="제주대회 지원금액을 입력해주세요"
									onChange={handlePriceInputChange}
									rules={{
										validate: (value) => {
											const currentJejudo = watch('local.jejudo')
											if (currentJejudo === true) {
												return value && value.trim() !== ''
													? true
													: '제주대회 지원금액을 입력해주세요.'
											}
											return true
										},
									}}
								/>
							</div>
							<div className={styles.sponsorCap}>
								<ToggleButtonGroupController
									control={control}
									name="local.sponsorCap"
									toggleFieldLabel="선수 스폰서 모자 착용"
									toggleOption={sponsorCapOption}
								/>
							</div>
						</div>
						<div className={styles.nextSection} style={{ justifyContent: 'flex-end' }}>
							<div className={styles.beforeSuccess}>
								<button
									className={styles.before}
									type="button"
									onClick={() => navigate(-1)}>
									이전
								</button>
								<button
									className={styles.success}
									type="button"
									disabled={!isLocalValid}
									onClick={() => {
										// 국내 데이터만 store에 저장하고 해외 페이지로 이동
										const currentData = getValues()
										actions.updateStore({ onboardingInput: currentData })
										setValue('preferWhere', 'global')
									}}>
									다음
								</button>
							</div>
						</div>
					</>
				)}
				{preferWhere === 'global' && (
					<>
						<div className={styles.displayRegion}>해외</div>
						<div className={styles.applyTax}>
							<ToggleButtonGroupController
								control={control}
								name="global.applyTax"
								isNoticeNeed={true}
								isNoticeNeedText={`세전 : 3.3% 제외 후 입금 예정\n세후 : 3.3% 제외된 실 입금 금액`}
								toggleFieldLabel="임금 세금 포함 여부"
								toggleOption={applyTaxOption}
								rules={{ required: '임금 세금포함여부를 선택해주세요' }}
							/>
						</div>
						<div className={styles.payPeriod}>
							<ToggleButtonGroupController
								control={control}
								name="global.payPeriod"
								toggleFieldLabel="임금 지급 주기"
								toggleOption={payPeriodOption}
								rules={{ required: '임금 지급 주기를 선택해주세요' }}
							/>
						</div>
						<div className={styles.cutPass}>
							<SimpleInputController
								control={control}
								name="global.cutPass"
								rules={{ required: '컷 통과 금액을 입력해주세요' }}
								inputLabel="컷통과"
								inputPlaceholder="컷 통과시 지급할 금액을 입력해주세요"
								onChange={handlePriceInputChange}
							/>
						</div>
						<div className={styles.cutOut}>
							<SimpleInputController
								control={control}
								name="global.cutOut"
								rules={{ required: '컷 탈락 금액을 입력해주세요' }}
								inputLabel="컷탈락"
								inputPlaceholder="컷 탈락시 지급할 금액을 입력해주세요"
								onChange={handlePriceInputChange}
							/>
						</div>
						<div className={styles.incentive}>
							<div className={styles.incentiveInfo}>
								인센티브
								<InformationCircleIcon
									className={styles.noticeIcon}
									data-tooltip-id="my-tooltip3"
									data-tooltip-content={`상금 표기액 : 각 투어별 홈페이지에 명시된 금액 \n 상금 실수령액 : KPGA 10%, KLPGA 9.3% 제외된 금액`}
								/>
								<Tooltip id="my-tooltip3" place="top" className={styles.tooltip} />
							</div>
							<button type="button" onClick={handleModalOpen}>
								입력하기
							</button>
						</div>
						<Controller
							name="global.incentiveSettings"
							control={control}
							rules={{
								validate: (value) =>
									value && value.length > 0
										? true
										: '인센티브 설정을 입력해주세요.',
							}}
							render={({ field }) => <input type="hidden" {...field} />}
						/>
						{activeData?.incentiveSettings &&
							activeData.incentiveSettings.length > 0 && (
								<div className={styles.incentiveDisplaySection}>
									<div className={styles.incentiveDisplayItem}>
										{formatIncentiveTaxOptionLabel(globalIncentiveOption)}
									</div>
									{activeData.incentiveSettings.map((setting) => {
										if (setting.percentage === null && setting.end === null)
											return null
										const displayRank =
											setting.id === 1
												? '우승 '
												: setting.start +
													(setting.end !== null
														? ` ~ ${setting.end} `
														: '~ ')
										const displayPercentage =
											setting.percentage !== null
												? `${setting.percentage}%`
												: '미입력'
										return (
											<div
												key={setting.id}
												className={styles.incentiveDisplayItem}>
												<span>{displayRank}</span>
												<span>: {displayPercentage}</span>
											</div>
										)
									})}
								</div>
							)}
						<div className={styles.overSeasSupport}>
							<Controller
								name="global.overseasSupport"
								control={control}
								rules={{ required: '해외대회 지원 항목을 선택해주세요' }}
								render={({ field }) => (
									<>
										<div className={styles.overSeasSupportTitle}>
											해외대회 지원 항목
										</div>
										<div
											className={styles.overSeasSupportContainer}
											ref={dropdownRef}>
											<div
												className={styles.overSeasSupportDropDownInput}
												onClick={toggleDropdown}
												role="button"
												aria-haspopup="listbox"
												aria-expanded={isDropdownOpen}>
												{field.value === undefined ||
												field.value === null ? (
													<span
														className={
															styles.overSeasSupportDropDownPlaceholder
														}>
														해외대회 지원 항목을 선택해주세요
													</span>
												) : (
													<span className={styles.overSeasSupportDisplay}>
														{field.value === 0
															? '미지원'
															: field.value + '%'}
													</span>
												)}
												<ChevronDownIcon className={styles.dropDownArrow} />
											</div>
											{isDropdownOpen && (
												<ul
													className={`${styles.dropDownList} ${isDropdownOpen ? styles.dropDownListOpened : styles.dropDownListClosed}`}>
													{overseasSupportOptions.map((option) => (
														<li
															key={option.value}
															className={styles.dropDownListItem}
															onClick={(e) => {
																e.stopPropagation()
																field.onChange(option.value)
																setIsDropdownOpen(false)
															}}>
															{option.label}
														</li>
													))}
												</ul>
											)}
										</div>
									</>
								)}
							/>
						</div>
						<div className={styles.sponsorCap}>
							<ToggleButtonGroupController
								control={control}
								name="global.sponsorCap" // 'global' 객체 내부의 고정된 이름
								toggleFieldLabel="선수 스폰서 모자 착용"
								toggleOption={sponsorCapOption}
							/>
						</div>
						<div className={styles.nextSection}>
							<button
								className={styles.skip}
								type="button"
								onClick={() => {
									// 해외 스킵 확인 모달 띄우기
									setIsOverseasMatchModalOpen(true)
								}}>
								Skip
							</button>
							<div className={styles.beforeSuccess}>
								<button
									className={styles.before}
									type="button"
									onClick={() => {
										setValue('preferWhere', 'local')
									}}>
									이전
								</button>
								<button
									className={styles.success}
									type="button"
									disabled={!isGlobalValid}
									onClick={() => {
										const currentData = getValues()
										onSubmit(currentData)
									}}>
									완료
								</button>
							</div>
						</div>
					</>
				)}
				{/* <div className={styles.nextSection}>
					<button
						className={styles.skip}
						type="button"
						onClick={() => navigate('/tour-pro/home')}>
						Skip
					</button>
					<div className={styles.beforeSuccess}>
						<button
							className={styles.before}
							type="button"
							onClick={() => navigate(-1)}>
							이전
						</button>
						<button
							className={styles.success}
							type="submit"
							disabled={!isOverallFormValid}
							onClick={handleSubmit(onSubmit)}>
							완료
						</button>
					</div>
				</div> */}
				<IncentiveModal
					isModalOpen={isModalOpen}
					onFinish={handleFinishSubmit}
					onReturn={handleReturn}
					initialSettings={activeData?.incentiveSettings}></IncentiveModal>
				<OverseasMatchModal
					isModalOpen={isOverseasMatchModalOpen}
					onReturn={handleOverseasMatchReturn}
					onSubmit={handleOverseasMatchSubmit}></OverseasMatchModal>
			</div>
		</div>
	)
}

export default OnboardingNext
